/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { isPropaneFuel, nearestSixteenth, NoteType, useSubscription } from '@rta/rta-blueprint-library';

import { SlackIcon } from '@components/icons';
import { lang } from '@lang';
import { createHubSpotBlueprint, HubSpotBlueprintPayload, saveQuote, saveCustomerBlueprint } from '@external';
import { getDesignImage } from '@helpers';
import { useAppToast, useDesign } from '@hooks';
import { customerState, projectState, selectNavigation, useAppSelector } from '@state';
import { Feature, FeatureHoookParams } from '..';
import { repointDetachedDraft } from '@features/customer/helpers';
import { useShareLinkFeature } from '@features/social';
import { useQuoteVerification } from './use-quote-verification.hook';
import { validateCrmOperation } from './validate-crm-operation';


// Maps kitchen fuelchoice to HubSpot's fuel_source enum ('Natural Gas' | 'Propane' | 'N/A').
// Only 'ng' is Natural Gas; propane variants are Propane; no-fuel/unset/unknown is N/A — never a silent NG default.
const getFuelDisplayName = (fuelchoice: string | null | undefined): string => {
	if (isPropaneFuel(fuelchoice)) return 'Propane';
	if ((fuelchoice ?? '').trim().toLowerCase() === 'ng') return 'Natural Gas';
	return 'N/A';
};

// Normalize layout value to match HubSpot's exact allowed options
const normalizeLayout = (layout: string | null | undefined): string => {
	if (!layout) return 'Non-Standard Layout';

	const normalized = layout.toLowerCase().trim();

	if (normalized === 'linear') return 'Linear';
	if (normalized.includes('l-shape') || normalized === 'l shaped') return 'L-Shaped';
	if (normalized === 'galley') return 'Galley';
	if (normalized.includes('u-shape') || normalized === 'u shaped') return 'U-shaped';
	if (normalized.includes('side-by-side') || normalized === 'side by side') return 'Side-by-side';
	return 'Non-Standard Layout';
};

// Maps library surround `displayName` directly to the HubSpot island_finish enum value.
// Keys are the literal displayName values produced by the surround asset map; do not transform.
const SURROUND_DISPLAY_NAME_TO_HUBSPOT: Record<string, string> = {
	'Stacked Stone - Graphite': 'Stacked Stone Graphite',
	'Stacked Stone - Terra': 'Stacked Stone Terra',
	'Stacked Stone - Chalk': 'Stacked Stone Chalk',
	'Modern Concrete - Bright': 'Modern Concrete Bright',
	'Modern Concrete - Industrial': 'Modern Concrete Industrial',
	'Modern Stone - Ash': 'Modern Stone / Ash',
	'Modern Stone - Bright': 'Modern Stone / Bright',
	'Reclaimed Brick - White': 'Reclaimed Brick White',
	'Weathered Wood - Ash': 'Weathered Wood Ash',
	'Weathered Wood - Umber': 'Weathered Wood Umber',
	'Plank - Charcoal': 'Plank Charcoal',
	'Plank - Bright': 'Plank Bright',
	'Slat - Bright': 'Slat Bright',
	'Slat - Charcoal': 'Slat Charcoal',
	'Slat - Walnut': 'Slat Walnut',
	'Scratch Coat': 'Scratch',
	'MOKS - Pure White': 'MOKs - Pure White',
	'MOKS - Slate Gray': 'MOKS - Slate Gray',
	'MOKS - Pitch Black': 'MOKs - Pitch Black'
};

// HubSpot's exact allowed island_finish options. A mapped value not in this set is downgraded to
// 'To Be Determined' so finish drift can never hard-fail the whole blueprint submit again.
export const HUBSPOT_ISLAND_FINISH_OPTIONS = new Set<string>([
	'To Be Determined', 'Stacked Stone Graphite', 'Stacked Stone Terra', 'Stacked Stone Chalk',
	'Modern Concrete Bright', 'Modern Concrete Industrial', 'Modern Stone / Ash', 'Modern Stone / Bright',
	'Reclaimed Brick White', 'Weathered Wood Ash', 'Weathered Wood Umber', 'Plank Charcoal', 'Plank Bright',
	'Slat Bright', 'Slat Charcoal', 'Slat Walnut', 'Cottage X | Bright', 'Cottage X | Charcoal', 'Scratch',
	'MOKs - Pure White', 'MOKS - Slate Gray', 'MOKs - Pitch Black', 'MOKs - Navy Marine', 'MOKs - Nordic Gray',
	'MOKs - Rubbed Bronze', 'MOKs - Steel Gray'
]);

const normalizeIslandFinish = (finish: string | null | undefined): string => {
	if (!finish) return 'To Be Determined';
	const mapped = SURROUND_DISPLAY_NAME_TO_HUBSPOT[finish] ?? 'To Be Determined';
	return HUBSPOT_ISLAND_FINISH_OPTIONS.has(mapped) ? mapped : 'To Be Determined';
};

// Maps library countertop `displayName` directly to the HubSpot countertop enum value.
// Keys are the literal displayName values produced by the countertop asset map; do not transform.
const COUNTERTOP_DISPLAY_NAME_TO_HUBSPOT: Record<string, string> = {
	'No Countertop': 'No Countertop',
	'Chiseled Edge - Smoky': 'Chiseled Smoky',
	'Chiseled Edge - Hickory': 'Chiseled Hickory',
	'Chiseled Edge - Cloudy': 'Chiseled Cloudy',
	'Clean Edge - Smoky': 'Clean Edge Smoky',
	'Clean Edge - Hickory': 'Clean Edge Hickory',
	'Clean Edge - Cloudy': 'Clean Edge Cloudy',
	'Clean Edge - Slate': 'Clean Edge Slate',
	'Granite - Fantasy Brown': 'Granite Fantasy Brown',
	'Granite - Steel Gray': 'Granite Steel Gray',
	'Granite - Venetian Gold': 'Granite Venetian Gold',
	'Granite - Viscount White': 'Granite Viscount White',
	'Granite - Alpha White': 'Granite Alpha White',
	'Granite - Thunder White': 'Granite Thunder White',
	'Granite - Giallo Ornamental White': 'Granite Giallo Ornamental White',
	'MOKS - Dove Shimmer': 'MOKS - Dove Shimmer',
	'MOKS - Galactic Ice': 'MOKS - Galactic Ice',
	'MOKS - Asteroid Terrazzo': 'MOKS - Asteroid Terrazo'
};

// HubSpot's exact allowed countertop options. A mapped value not in this set is downgraded to
// 'To Be Determined' so countertop drift can never hard-fail the whole blueprint submit.
export const HUBSPOT_COUNTERTOP_OPTIONS = new Set<string>([
	'No Countertop', 'To Be Determined', 'Chiseled Smoky', 'Chiseled Hickory', 'Chiseled Cloudy',
	'Clean Edge Smoky', 'Clean Edge Hickory', 'Clean Edge Cloudy', 'Granite Viscount White',
	'Granite Steel Gray', 'Granite Venetian Gold', 'Granite Alpha White', 'Granite Thunder White',
	'Granite Giallo Ornamental White', 'MOKS - Asteroid Terrazo', 'MOKS - Dove Shimmer', 'MOKS - Galactic Ice',
	'MOKs - Everest', 'MOKs - Elegant Gray', 'MOKs - Dove Gray', 'MOKs - Antartica', 'MOKs - Neutral Concrete',
	'MOKs - Silver Gray'
]);

const normalizeCountertop = (countertop: string | null | undefined): string => {
	if (!countertop) return 'To Be Determined';
	const mapped = COUNTERTOP_DISPLAY_NAME_TO_HUBSPOT[countertop] ?? 'To Be Determined';
	return HUBSPOT_COUNTERTOP_OPTIONS.has(mapped) ? mapped : 'To Be Determined';
};

// payment_type is seeded from the deal (project[0].paymentType), which is not guaranteed to match
// HubSpot's option set — clamp it so a stray value can't hard-fail the submit.
const HUBSPOT_PAYMENT_TYPES = new Set<string>(['Normal', 'Split payment', 'Delayed Payment', 'Paid in full', 'No payment (ambassador orders)']);

const normalizePaymentType = (paymentType: string | null | undefined): string => (
	paymentType && HUBSPOT_PAYMENT_TYPES.has(paymentType) ? paymentType : 'Normal'
);

export type PassoffChecklistState = {
	fuelSource: boolean;
	islandDimensions: boolean[];
	countertopDimensions: boolean[];
	utilityCutouts: boolean;
};

export type IslandDimensionInfo = {
	name: string;
	width: number;
	height: number;
	depth: number;
};

export type IslandOverhangInfo = {
	name: string;
	overhangs: Record<string, number>;
};

export type CountertopDimensionInfo = {
	name: string;
	width: number;
	depth: number;
};

export type BlueprintDisplayValues = {
	fuelSource: string;
	islandDimensions: IslandDimensionInfo[];
	countertopDimensions: CountertopDimensionInfo[];
	islandOverhangs: IslandOverhangInfo[];
};

export type HubSpotBlueprintFormState = {
	blueprint_name: string;
	standard___custom: string;
	island_finish: string;
	kitchen_layout: string;
	island_quantity: string;
	island_length: string;
	island_location: string;
	countertop: string;
	countertop_overhang: string;
	contains_non_coyote_appliances: string;
	fuel_source: string;
	cutout_options_2: Array<string>;
	future_project_approval_timeline: string;
	delayed_delivery: string;
	install_method: string;
	payment_type: string;
	project_folder: string;
	additional_details: string;
	css_notes: string;
	details_to_confirmed: string;
	contact_name: string;
	contact_email: string;
	// Dependent fields
	island_height: string;
	contains_corner_stand: string;
	l_shape_is_offset: string;
	projected_delivery_date: string;
	blueprint_delayed: string;
	delayed_reason: string;
	on_hold_reason: string;
	on_hold_source: string;
	projected_off_hold_date: string;
};

export type PassoffModalData = {
	checklist: PassoffChecklistState;
	projectFolder: string;
	cutoutOptions: string;
	additionalDetails: string;
	isSubmitting: boolean;
	blueprintValues: BlueprintDisplayValues;
	hubspotForm: HubSpotBlueprintFormState;
};

type PassoffFeatureParams = FeatureHoookParams;

export const usePassoffFeature = (_params: PassoffFeatureParams = {}): Feature<PassoffModalData> => {
	const { getToken } = useKeystoneSession();
	const [controller, design] = useDesign();

	const [sizeEvent] = useSubscription(controller?.kitchen ?? null, false, [
		['kitchen_fuel', '*'],
		['surround_material', '*'],
		['countertop_material', '*'],
		['kitchen_notes', '*'],
		['island_size', '*'],
		['panel_size', '*'],
		['countertop', '*'],
		['overhang', '*']
	]);

	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);
	const appNav = useAppSelector(selectNavigation);
	const link = useShareLinkFeature().value;

	const appToast = useAppToast();

	const [success, setSuccess] = useState<boolean | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [checklist, setChecklist] = useState<PassoffChecklistState>({
		fuelSource: false,
		islandDimensions: [],
		countertopDimensions: [],
		utilityCutouts: false
	});

	// Legacy state (keeping for compatibility)
	const [projectFolder, setProjectFolder] = useState<string>('');
	const [cutoutOptions, setCutoutOptions] = useState<string>('');
	const [additionalDetails, setAdditionalDetails] = useState<string>('');
	const [quoteName, setQuoteName] = useState<string>('');
	const [submitQuote, setSubmitQuote] = useState<boolean>(true);

	// HubSpot Blueprint form state - all fields editable
	const [hubspotForm, setHubspotForm] = useState<HubSpotBlueprintFormState>({
		blueprint_name: '',
		standard___custom: 'Standard',
		island_finish: '',
		kitchen_layout: '',
		island_quantity: '',
		island_length: 'All islands are 8ft or shorter',
		island_location: 'Free Standing',
		countertop: '',
		countertop_overhang: 'Standard Overhang',
		contains_non_coyote_appliances: 'false',
		fuel_source: 'N/A',
		cutout_options_2: ['None'],
		future_project_approval_timeline: 'Immediate',
		delayed_delivery: 'false',
		install_method: 'Customer Undecided',
		payment_type: 'Normal',
		project_folder: '',
		additional_details: '',
		css_notes: '',
		details_to_confirmed: 'None',
		contact_name: '',
		contact_email: '',
		// Dependent fields
		island_height: '',
		contains_corner_stand: 'false',
		l_shape_is_offset: 'false',
		projected_delivery_date: '',
		blueprint_delayed: '',
		delayed_reason: '',
		on_hold_reason: '',
		on_hold_source: '',
		projected_off_hold_date: ''
	});

	const [isModalOpen, setIsModalOpen] = useState(false);

	const [passoffStep, setPassoffStep] = useState<'deal-selection' | 'verification' | 'checklist'>('deal-selection');
	const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

	const {
		isVerifying,
		verifiedItems,
		summary: verificationSummary,
		verifyItems: runVerification,
		handlePriceSelectionChange,
		resetVerification,
		buildPriceOverrides
	} = useQuoteVerification();

	const dealId = selectedDealId;

	const defaultQuoteName = appNav.designId || `${customer.firstName} ${customer.lastName}`.trim();

	const autoFillHubspotForm = useCallback(() => {
		const kitchen = controller?.kitchen;
		const islands = kitchen?.design?.islands?.children ?? [];
		const islandCount = islands.length;

		const hasLongIsland = islands.some(island => (island.size?.width ?? 0) > 96);
		const islandLength = hasLongIsland
			? 'At least one island is longer than 8ft'
			: 'All islands are 8ft or shorter';

		const obstructions = kitchen?.design?.obstructions?.children ?? [];
		const hasWallObstruction = obstructions.length > 0;
		const islandLocation = hasWallObstruction ? 'Against Wall' : 'Free Standing';

		// SKU-annotation text boxes also write themselves into kitchen.notes (typed
		// NoteType.Annotation); exclude them so only designer-authored notes flow into
		// the HubSpot Additional Details field.
		const commentsData = (kitchen?.notes?.children ?? []).filter(c => c.type !== NoteType.Annotation);
		const formattedComments = commentsData.length > 0
			? commentsData.map(c => `• ${c.message}`).join('\n')
			: '';

		const normalizedLayout = normalizeLayout(kitchen?.kitchenLayout && kitchen.kitchenLayout.trim() ? kitchen.kitchenLayout : kitchen?.layout);

		// Auto-detect corner stands from island data
		const hasCornerStand = islands.some((island) => island.isCornerIsland);

		// Auto-detect L-shape offset from connection offsets
		const hasLShapeOffset = islands.some((island) => {
			const sourceConnections = island.connections?.getSourceConnections?.() ?? [];
			return sourceConnections.some((conn) => conn.offset !== 0);
		});

		// Auto-detect island heights from island types
		const hasBarHeight = islands.some((island) => island.type === 'BAR');
		const hasGrillHeight = islands.some((island) => island.type === 'GRILL' || island.type === 'SMOKER' || island.type === 'PIZZA' || island.type === 'ASADO' || island.type === 'CORNER');
		const autoIslandHeight = (() => {
			if (normalizedLayout === 'Linear') return '';
			if (hasBarHeight && hasGrillHeight) return 'both';
			if (hasBarHeight) return 'bar_height';
			return 'grill_height';
		})();

		setHubspotForm({
			blueprint_name: `${customer.firstName} ${customer.lastName}`.trim(),
			standard___custom: 'Standard',
			island_finish: normalizeIslandFinish(kitchen?.material?.surround?.displayName),
			kitchen_layout: normalizedLayout,
			island_quantity: islandCount > 3 ? '4+' : (islandCount > 0 ? islandCount.toString() : '1'),
			island_length: islandLength,
			island_location: islandLocation,
			countertop: normalizeCountertop(kitchen?.material?.countertop?.displayName),
			countertop_overhang: 'Standard Overhang',
			contains_non_coyote_appliances: 'false',
			fuel_source: getFuelDisplayName(kitchen?.fuelchoice),
			cutout_options_2: ['None'],
			future_project_approval_timeline: project[0]?.timeline === 'Immediate' ? 'Immediate' : 'Immediate',
			delayed_delivery: 'false',
			install_method: 'Customer Undecided',
			payment_type: normalizePaymentType(project[0]?.paymentType),
			project_folder: '',
			additional_details: formattedComments,
			css_notes: '',
			details_to_confirmed: 'None',
			contact_name: `${customer.firstName} ${customer.lastName}`.trim(),
			contact_email: customer.email,
			// Dependent fields - auto-fill where possible
			island_height: autoIslandHeight,
			contains_corner_stand: hasCornerStand ? 'true' : 'false',
			l_shape_is_offset: hasLShapeOffset ? 'true' : 'false',
			projected_delivery_date: '',
			blueprint_delayed: '',
			delayed_reason: '',
			on_hold_reason: '',
			on_hold_source: '',
			projected_off_hold_date: ''
		});
	}, [controller?.kitchen, customer, project]);

	// Success handler - reset form state when success is true
	useEffect(() => {
		if (success) {
			setSuccess(null);
			setIsModalOpen(false);
			setChecklist({ fuelSource: false, islandDimensions: [], countertopDimensions: [], utilityCutouts: false });
			setProjectFolder('');
			setCutoutOptions('');
			setAdditionalDetails('');
			setQuoteName('');
			setSubmitQuote(true);
			setPassoffStep('deal-selection');
			setSelectedDealId(null);
			resetVerification();
		}
	}, [success, resetVerification]);

	// Error handler
	useEffect(() => {
		if (error !== null) {
			appToast.error({ title: error });
			setError(null);
		}
	}, [error, appToast]);

	const startVerification = useCallback(async (): Promise<void> => {
		setPassoffStep('verification');
		const msrp = controller?.kitchen?.itemizedMsrp;
		const promotions = controller?.kitchen?.promotionalSkus?.children ?? null;
		if (msrp) {
			await runVerification({
				islands: msrp.islands ?? null,
				countertops: msrp.countertops ?? [],
				backsplashes: msrp.backsplashes ?? [],
				items: msrp.items ?? [],
				accessories: msrp.accessories ?? []
			}, promotions);
		}
	}, [runVerification, controller]);

	const continueToChecklist = useCallback(() => {
		setPassoffStep('checklist');
	}, []);

	const backToVerification = useCallback(() => {
		setPassoffStep('verification');
	}, []);

	const allChecklistItemsChecked = useMemo(() => {
		const { fuelSource, islandDimensions, countertopDimensions, utilityCutouts } = checklist;
		if (!fuelSource || !utilityCutouts) return false;
		if (islandDimensions.length === 0 || !islandDimensions.every(Boolean)) return false;
		if (countertopDimensions.length === 0 || !countertopDimensions.every(Boolean)) return false;
		return true;
	}, [checklist]);

	const willSubmitQuote = allChecklistItemsChecked && !isSubmitting;

	const comments = useMemo(() => {
		return (controller?.kitchen?.notes?.children ?? []).filter(c => c.type !== NoteType.Annotation);
	}, [controller?.kitchen?.notes?.children]);

	const blueprintValues = useMemo((): BlueprintDisplayValues => {
		const kitchen = controller?.kitchen;
		if (!kitchen) {
			return {
				fuelSource: 'N/A',
				islandDimensions: [],
				countertopDimensions: [],
				islandOverhangs: []
			};
		}

		const fuelSource = getFuelDisplayName(kitchen.fuelchoice);

		const islands = kitchen.design?.islands?.children ?? [];
		const islandDimensions: IslandDimensionInfo[] = islands.map((island, index) => {
			const size = island.islandSize;
			if (island.isCornerIsland) {
				const frontPanel = island.panels?.getFrontPanel?.();
				return {
					name: island.description || `Island ${index + 1}`,
					width: frontPanel?.size?.width ?? 0,
					height: size?.height ?? 0,
					depth: 0
				};
			}
			return {
				name: island.description || `Island ${index + 1}`,
				width: size?.width ?? 0,
				height: size?.height ?? 0,
				depth: size?.depth ?? 0
			};
		});

		const countertopDimensions: CountertopDimensionInfo[] = islands.map((island, index) => {
			if (island.isCornerIsland) {
				const frontPanel = island.panels?.getFrontPanel?.();
				const countertop = kitchen.design?.countertops?.children?.find(
					(ct) => ct.islandIds?.includes(island.id)
				);
				const frontCountertopWidth = frontPanel && countertop
					? nearestSixteenth(countertop.getCountertopWidthForPanel?.(frontPanel.id) ?? 0)
					: 0;
				return {
					name: island.description || `Island ${index + 1}`,
					width: frontCountertopWidth,
					depth: 0
				};
			}
			const overallSize = island.overallSize;
			return {
				name: island.description || `Island ${index + 1}`,
				width: overallSize?.width ?? 0,
				depth: overallSize?.depth ?? 0
			};
		});

		const islandOverhangs: IslandOverhangInfo[] = islands.map((island, index) => {
			return {
				name: island.description || `Island ${index + 1}`,
				overhangs: island.getOverhangs?.() ?? { front: 0, back: 0, left: 0, right: 0 }
			};
		});

		return {
			fuelSource,
			islandDimensions,
			countertopDimensions,
			islandOverhangs
		};
	}, [
		controller?.kitchen,
		controller?.kitchen?.fuelchoice,
		controller?.kitchen?.material?.surround,
		controller?.kitchen?.material?.countertop,
		sizeEvent
	]);

	// Initialize checklist arrays when blueprint values change
	useEffect(() => {
		const islandCount = blueprintValues.islandDimensions.length;
		const countertopCount = blueprintValues.countertopDimensions.length;

		setChecklist((prev) => ({
			...prev,
			islandDimensions: prev.islandDimensions.length === islandCount
				? prev.islandDimensions
				: new Array(islandCount).fill(false),
			countertopDimensions: prev.countertopDimensions.length === countertopCount
				? prev.countertopDimensions
				: new Array(countertopCount).fill(false)
		}));
	}, [blueprintValues.islandDimensions.length, blueprintValues.countertopDimensions.length]);

	const [disabled, tooltip] = (() => {
		if (customer.email === '') return [true, lang.crm.tooltip.passOffMissingCustomer];
		if (project.length === 0) return [true, lang.document.tooltip.missingHubspotData];

		return [false, lang.crm.tooltip.passOff];
	})();

	const isSubmitDisabled = !allChecklistItemsChecked || !hubspotForm.project_folder.trim() || isSubmitting;

	const updateChecklist = useCallback((key: 'fuelSource' | 'utilityCutouts', value: boolean) => {
		setChecklist((prev) => ({ ...prev, [key]: value }));
	}, []);

	const updateChecklistArray = useCallback((key: 'islandDimensions' | 'countertopDimensions', index: number, value: boolean) => {
		setChecklist((prev) => {
			const newArray = [...prev[key]];
			newArray[index] = value;
			return { ...prev, [key]: newArray };
		});
	}, []);

	const updateProjectFolder = useCallback((newProjectFolder: string) => {
		setProjectFolder(newProjectFolder);
		setHubspotForm(prev => ({ ...prev, project_folder: newProjectFolder }));
	}, []);

	const updateCutoutOptions = useCallback((newCutoutOptions: string) => {
		setCutoutOptions(newCutoutOptions);
		setHubspotForm(prev => ({ ...prev, cutout_options_2: newCutoutOptions ? newCutoutOptions.split(';') : ['None'] }));
	}, []);

	const updateAdditionalDetails = useCallback((newAdditionalDetails: string) => {
		setAdditionalDetails(newAdditionalDetails);
		setHubspotForm(prev => ({ ...prev, additional_details: newAdditionalDetails }));
	}, []);

	const handleQuoteNameChange = useCallback((name: string): void => {
		setQuoteName(name);
	}, []);

	const handleSubmitQuoteChange = useCallback((value: boolean): void => {
		setSubmitQuote(value);
	}, []);

	const updateHubspotFormField = useCallback(<K extends keyof HubSpotBlueprintFormState>(
		field: K,
		value: HubSpotBlueprintFormState[K]
	) => {
		setHubspotForm(prev => ({ ...prev, [field]: value }));
	}, []);

	const handleDealSelect = useCallback((newDealId: string): void => {
		setSelectedDealId(newDealId);
	}, []);

	const submitPassoff = useCallback(async () => {
		if (!controller || isSubmitDisabled) return;

		// Validate required CRM fields before proceeding
		const validation = validateCrmOperation({ customer, project, selectedDealId: dealId });
		if (!validation.canProceed) {
			appToast.error({
				title: 'Cannot submit blueprint',
				description: validation.blockingErrors.join('\n')
			});
			return;
		}

		if (validation.warningMessage) {
			appToast.warning({
				title: 'Blueprint will be submitted with missing fields',
				description: validation.warningMessage
			});
		}

		setIsSubmitting(true);

		const currentUrl = window.location.href;

		const payload: HubSpotBlueprintPayload = {
			dealId: dealId ?? '',
			thumbnail: getDesignImage(controller?.kitchen?.context?.blueprint) || undefined,
			properties: {
				blueprint_name: hubspotForm.blueprint_name,
				standard___custom: hubspotForm.standard___custom || undefined,
				island_finish: hubspotForm.island_finish || undefined,
				kitchen_layout: hubspotForm.kitchen_layout || undefined,
				island_quantity: hubspotForm.island_quantity || undefined,
				island_length: hubspotForm.island_length || undefined,
				island_location: hubspotForm.island_location || undefined,
				countertop: hubspotForm.countertop || undefined,
				countertop_overhang: hubspotForm.countertop_overhang || undefined,
				contains_non_coyote_appliances: hubspotForm.contains_non_coyote_appliances || undefined,
				fuel_source: hubspotForm.fuel_source || undefined,
				cutout_options_2: hubspotForm.cutout_options_2.length > 0 ? hubspotForm.cutout_options_2.join(';') : undefined,
				future_project_approval_timeline: hubspotForm.future_project_approval_timeline || undefined,
				delayed_delivery: hubspotForm.delayed_delivery || undefined,
				install_method: hubspotForm.install_method || undefined,
				payment_type: hubspotForm.payment_type || undefined,
				project_folder: hubspotForm.project_folder || undefined,
				additional_details: hubspotForm.additional_details || undefined,
				css_notes: hubspotForm.css_notes || undefined,
				details_to_confirmed: hubspotForm.details_to_confirmed || undefined,
				contact_name: hubspotForm.contact_name || undefined,
				contact_email: hubspotForm.contact_email || undefined,
				blueprint_tool_image: currentUrl || undefined,
				// Dependent fields - only send when relevant
				island_height: hubspotForm.island_height || undefined,
				contains_corner_stand: hubspotForm.contains_corner_stand || undefined,
				l_shape_is_offset: hubspotForm.l_shape_is_offset || undefined,
				projected_delivery_date: hubspotForm.projected_delivery_date || undefined,
				blueprint_delayed: hubspotForm.blueprint_delayed || undefined,
				delayed_reason: hubspotForm.delayed_reason || undefined,
				on_hold_reason: hubspotForm.on_hold_reason || undefined,
				on_hold_source: hubspotForm.on_hold_source || undefined,
				projected_off_hold_date: hubspotForm.projected_off_hold_date || undefined
			}
		};

		await createHubSpotBlueprint({
			payload,
			getToken,
			onSuccess: async () => {
				// Also save the quote to HubSpot, unless the user opted out for this submission
				let quoteSuccess = false;
				if (submitQuote) {
					try {
						const passoffDeal = project.find((d) => d.dealId === dealId) ?? project[0];
						const [quoteId, quoteError] = await saveQuote({
							controller,
							customer,
							dealAddress: passoffDeal ? { address: passoffDeal.address, city: passoffDeal.city, state: passoffDeal.state, zip: passoffDeal.zip } : undefined,
							dealId: dealId ?? '',
							link: link ?? '',
							getToken,
							setQuoteId: (id) => {
								controller.kitchen.quoteId = id;
								quoteSuccess = true;
							},
							setError: (err) => {
								console.error('Quote submission error:', err);
							},
							priceOverrides: buildPriceOverrides(),
							quoteName: quoteName || defaultQuoteName
						});

						if (quoteId) {
							quoteSuccess = true;
						}
					} catch (quoteErr) {
						console.error('Quote submission failed:', quoteErr);
					}
				}

				let versionSaveSuccess = false;
				if (quoteSuccess || !submitQuote) {
					try {
						const kitchens = controller.kitchen.context.blueprint?.kitchens;
						if (kitchens) {
							const draft = kitchens.findVersion(null);
							if (draft) {
								kitchens.promoteDraft();
							} else {
								kitchens.newVersionFromLatest();
							}
						}

						await saveCustomerBlueprint({
							controller,
							customerEmail: appNav.email ?? customer.email,
							mode: 'live',
							getToken,
							setSuccess: () => {
								versionSaveSuccess = true;
								appToast.success({ title: lang.customer.message.saveSuccess });
							},
							setError: (err) => {
								console.error('Version save error (non-blocking):', err);
							}
						});
					} catch (versionErr) {
						console.error('Version save failed (non-blocking):', versionErr);
					} finally {
						repointDetachedDraft(design);
					}
				}

				appToast.success({
					title: quoteSuccess
						? lang.crm.message.passOffAndQuoteSent
						: lang.crm.message.passOffSent,
					...(quoteSuccess ? { description: lang.crm.message.quoteLineItemsAutomated, persistent: true } : {})
				});

				setSuccess(true);
				setIsSubmitting(false);
			},
			onError: (err) => {
				setError(lang.crm.message.passOffFailed);
				setIsSubmitting(false);
			}
		});
	}, [controller, customer, dealId, getToken, isSubmitDisabled, hubspotForm, link, appToast, appNav, buildPriceOverrides, quoteName, defaultQuoteName, submitQuote]);

	return {
		name: 'passoff',
		label: lang.crm.label.passOff,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <SlackIcon {...props} />,
		disabled,
		value: {
			checklist,
			projectFolder,
			cutoutOptions,
			additionalDetails,
			isSubmitting,
			blueprintValues,
			hubspotForm
		},
		hidden: false,
		modal: [
			{
				isOpen: isModalOpen,
				data: {
					checklist,
					projectFolder,
					cutoutOptions,
					additionalDetails,
					isSubmitting,
					isSubmitDisabled,
					updateChecklist,
					updateChecklistArray,
					updateProjectFolder,
					updateCutoutOptions,
					updateAdditionalDetails,
					submitPassoff,
					allChecklistItemsChecked,
					willSubmitQuote,
					blueprintValues,
					comments,
					// Deal selection data
					deals: project,
					selectedDealId,
					handleDealSelect,
					startVerification,
					// Verification data
					passoffStep,
					isVerifying,
					verifiedItems,
					verificationSummary,
					continueToChecklist,
					backToVerification,
					handlePriceSelectionChange,
					quoteName,
					defaultQuoteName,
					handleQuoteNameChange,
					submitQuote,
					handleSubmitQuoteChange,
					hubspotForm,
					updateHubspotFormField
				}
			},
			() => {
				setIsModalOpen(!isModalOpen);
				if (isModalOpen) {
					setPassoffStep('deal-selection');
					setSelectedDealId(null);
					resetVerification();
				}
			}
		],
		handler: async () => {
			setIsModalOpen(true);
			setQuoteName(defaultQuoteName);
			autoFillHubspotForm();

			// Pre-populate deal from kitchen.dealId if it matches a project deal
			const isBlueprintController = controller instanceof BpController;
			const kitchenDealId = isBlueprintController ? controller.kitchen.dealId : '';
			const preSelectedDeal = kitchenDealId ? project.find((d) => d.dealId === kitchenDealId) : null;

			if (preSelectedDeal) {
				setSelectedDealId(preSelectedDeal.dealId);
				await startVerification();
			} else if (project.length === 1) {
				setSelectedDealId(project[0].dealId);
				await startVerification();
			} else {
				setPassoffStep('deal-selection');
				setSelectedDealId(null);
			}
		}
	};
};
